import { useTranslation } from 'react-i18next';
import { Paper, Progress, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import {
  countVehiclesAllocated,
  countVehiclesLoaded,
  countVehiclesUnloaded,
  type ForwardingStageKey,
  percent as toPercent,
  remaining,
} from '@/lib/forwardingView';
import { formatMoney } from '@/lib/format';
import type { ForwardingInfo } from '@/types/deals';

interface ForwardingFunnelProps {
  readonly info: ForwardingInfo;
  /** Активный этап (подсветка карточки) — синхронизирован с активным табом. */
  readonly active?: ForwardingStageKey;
  /** Клик по карточке → переключение на таб этапа. */
  readonly onSelect?: (key: ForwardingStageKey) => void;
}

interface Stage {
  readonly key: ForwardingStageKey;
  readonly label: string;
  readonly done: number | undefined;
  readonly total: number | undefined;
  readonly remaining?: number;
  readonly unit: string;
  readonly count: number;
}

// Дашборд-воронка: Разобрано (т) → Отгружено (кг) → В пути (кг) → Доставлено (кг). Карточки кликабельны
// (переключают таб этапа), активная подсвечена; этап без данных — приглушён с «—».
export default function ForwardingFunnel({ info, active, onSelect }: ForwardingFunnelProps) {
  const { t } = useTranslation();
  const planned = info.plannedDeliveryInfo;
  const loaded = info.loadedDeliveryInfo;
  const ongoing = info.ongoingDeliveryInfo;
  const unloaded = info.unloadedDeliveryInfo;
  const tons = t('app.deals.tons');
  const kg = t('app.deals.forwarding.kg');

  const stages: Stage[] = [
    {
      key: 'allocated',
      label: t('app.deals.forwarding.allocated'),
      done: planned?.disassembledWeightTon,
      total: planned?.weightTon,
      remaining: planned?.remainingWeightTon,
      unit: tons,
      count: countVehiclesAllocated(info),
    },
    {
      key: 'loaded',
      label: t('app.deals.forwarding.loaded'),
      done: loaded?.loadedWeightKg,
      total: loaded?.totalWeightKg,
      remaining: remaining(loaded?.totalWeightKg, loaded?.loadedWeightKg),
      unit: kg,
      count: countVehiclesLoaded(info),
    },
    {
      key: 'ongoing',
      label: t('app.deals.forwarding.ongoing'),
      done: ongoing?.ongoingWeightKg,
      total: loaded?.loadedWeightKg,
      unit: kg,
      count: ongoing?.vehicles?.length ?? 0,
    },
    {
      key: 'unloaded',
      label: t('app.deals.forwarding.unloaded'),
      done: unloaded?.unloadedWeightKg,
      total: unloaded?.totalWeightKg,
      remaining: remaining(unloaded?.totalWeightKg, unloaded?.unloadedWeightKg),
      unit: kg,
      count: countVehiclesUnloaded(info),
    },
  ];

  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
      {stages.map((stage) => {
        const hasData = stage.done != null || (stage.total != null && stage.total > 0) || stage.count > 0;
        const isActive = active === stage.key;
        return (
          <UnstyledButton
            key={stage.key}
            onClick={() => onSelect?.(stage.key)}
            aria-pressed={isActive}
            aria-label={stage.label}
            w="100%"
          >
            <Paper
              withBorder
              radius="md"
              p="sm"
              bg={isActive ? 'brand.0' : hasData ? undefined : 'gray.0'}
              style={{ borderColor: isActive ? 'var(--mantine-color-brand-5)' : undefined, height: '100%' }}
            >
              <Stack gap={6}>
                <Text fz="xs" c="gray.7" fw={500}>
                  {stage.label}
                </Text>
                {hasData ? (
                  <>
                    <Text fz="lg" fw={700} style={{ lineHeight: 1.1 }}>
                      {formatMoney(stage.done)}{' '}
                      <Text span fz="xs" c="gray.6" fw={500}>
                        {stage.unit}
                      </Text>
                    </Text>
                    <Progress value={toPercent(stage.done, stage.total)} size="xs" radius="xl" color="brand" />
                    <StageMeta
                      stage={stage}
                      remainingLabel={t('app.deals.forwarding.remainingShort')}
                      vehiclesLabel={t('app.deals.forwarding.vehiclesShort')}
                    />
                  </>
                ) : (
                  <>
                    <Text fz="lg" fw={700} c="gray.4" style={{ lineHeight: 1.1 }}>
                      —
                    </Text>
                    <Text fz="xs" c="gray.5">
                      {t('app.deals.forwarding.noStageData')}
                    </Text>
                  </>
                )}
              </Stack>
            </Paper>
          </UnstyledButton>
        );
      })}
    </SimpleGrid>
  );
}

function StageMeta({
  stage,
  remainingLabel,
  vehiclesLabel,
}: {
  readonly stage: Stage;
  readonly remainingLabel: string;
  readonly vehiclesLabel: string;
}) {
  const parts: string[] = [];
  if (stage.count > 0) {
    parts.push(`${stage.count} ${vehiclesLabel}`);
  }
  if (stage.remaining != null && stage.remaining > 0) {
    parts.push(`${remainingLabel} ${formatMoney(stage.remaining)} ${stage.unit}`);
  }
  if (parts.length === 0) {
    return null;
  }
  return (
    <Text fz="xs" c="gray.6">
      {parts.join(' · ')}
    </Text>
  );
}
